import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Crown, Gift } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { GiftStatusPanel, SubscriptionPanel, UpgradePanel } from "@/components/BillingPanels";

const Usage = ({ label, used, limit, pro, testId }) => (
  <div data-testid={testId}>
    <div className="flex justify-between text-sm mb-1.5">
      <span className="text-slate-300">{label}</span>
      <span className="font-code text-slate-400">{pro ? `${used} · unlimited` : `${used} / ${limit}`}</span>
    </div>
    <Progress value={pro ? 100 : Math.min(100, (used / limit) * 100)} className="h-1.5 bg-line" />
  </div>
);

export default function BillingPage() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sp] = useSearchParams();
  const initialInterval = sp.get("interval") === "year" ? "year" : "month";
  useEffect(() => {
    api.get("/billing/status", { params: { sync: true } })
      .then((r) => { setS(r.data); refreshUser(); })
      .catch((e) => { toast.error(formatApiError(e)); nav("/worlds"); });
  }, [nav, refreshUser]);

  const toggleCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(s.cancel_at_period_end ? "/billing/resume" : "/billing/cancel");
      setS(data);
      await refreshUser();
      toast.success(data.cancel_at_period_end ? "Subscription will end at period close" : "Subscription resumed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <Loader />;

  return (
    <div className="relative z-10 min-h-screen px-6 md:px-12 py-10 max-w-5xl mx-auto" data-testid="billing-page">
      <button onClick={() => nav("/worlds")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 mb-10" data-testid="billing-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to worlds
      </button>
      <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3">Your plan</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-12">
        {s.is_pro ? "You wield the Pro codex" : "Unbind your worlds"}
      </h1>
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <div className="rounded-2xl border border-line bg-panel p-6 space-y-5" data-testid="usage-card">
          <h2 className="font-display text-2xl text-amber-100">Current usage</h2>
          <Usage label="Worlds" used={s.worlds_used} limit={s.worlds_limit} pro={s.is_pro} testId="usage-worlds" />
          {!s.is_pro && <p className="text-xs text-slate-500">Free plan: up to {s.worlds_limit} worlds, with the dark and light themes. Pro adds every theme, custom themes and scene break designs.</p>}
        </div>
        <div className="rounded-2xl border border-amber-500/40 bg-panel p-6 shadow-[0_0_30px_rgba(245,158,11,0.15)] space-y-5" data-testid="pro-plan-card">
          <div className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /><h2 className="font-display text-2xl text-amber-100">Lorely Pro</h2></div>
          {s.source === "subscription" ? <SubscriptionPanel s={s} onToggleCancel={toggleCancel} busy={busy} />
            : s.source === "gift" ? <GiftStatusPanel s={s} />
            : <UpgradePanel s={s} initialInterval={initialInterval} />}
          {s.source !== "gift" && <p className="text-[11px] text-slate-500">Secure checkout by Stripe. Cancel anytime. {!s.is_pro && <Link to="/worlds" className="hover:underline">Maybe later</Link>}</p>}
        </div>
      </div>
      <Link to="/gift" className="mt-6 group flex items-center justify-between gap-4 rounded-2xl border border-line bg-panel p-5 hover:border-amber-500/40 transition-colors" data-testid="billing-gift-link">
        <span className="flex items-center gap-3">
          <Gift className="w-5 h-5 text-amber-400" />
          <span><span className="block font-display text-xl text-amber-100">Gift Pro</span>
            <span className="text-xs text-slate-400">Give a friend, co-author or beta reader a year of unlimited worlds for ${s.gift_price}.</span></span>
        </span>
        <span className="text-sm text-amber-300 group-hover:translate-x-1 transition-transform">Send a gift →</span>
      </Link>
    </div>
  );
}
